import React from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { OutlinedInput } from '@mui/material';
import styles from './UpdateCommitmentDialog.module.scss';
import APIClient from '../../../../util/APIClient';
import { isNumeric } from '../../../../hooks/useTreeEditor';
import FormAlert from '../FormAlert/FormAlert';

export default function UpdateCommitmentDialog({
  commitment,
  isOpen,
  close,
  reload = () => {},
}) {
  const [valueInput, setValueInput] = React.useState('');
  const [valueError, setValueError] = React.useState(null);
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (valueInput !== '') {
      if (!isNumeric(valueInput)) {
        setValueError('Value must be a number.');
        return;
      }
    }
    setValueError(null);
  }, [valueInput]);

  const handleClose = () => {
    setValueInput('');
    setValueError(null);
    setFormAlert(null);
    setIsSubmitting(false);
    close();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.patch(`/user/commitments/${commitment.id}`, {
        value: valueInput,
      });
      reload();
      handleClose();
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not update commitment!',
        message:
          'Something went wrong when updating the commitment. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    handleClose();
  };

  const handleFocusLost = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleFocusLost}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" className={styles.title}>
        Update Commitment
      </DialogTitle>
      <div className={styles.content}>
        <DialogContentText id="alert-dialog-description">
          You are about to update the completion state of
          <span style={{ fontStyle: 'italic', paddingLeft: '.2rem' }}>
            {commitment.label}
          </span>
          . This will only work if the commitment has not yet expired. The
          current value is {commitment.value}.
        </DialogContentText>
        <div className={styles.form}>
          <FormControl
            sx={{ m: 1 }}
            error={!!valueError}
            // disabled={!isEditable}
            className={styles.formControl}
          >
            <InputLabel id="label-label">Value</InputLabel>
            <OutlinedInput
              className={styles.input}
              id="value-input"
              label="Value"
              value={valueInput}
              onChange={(e) => setValueInput(e.target.value)}
            />
            {valueError && <FormHelperText>{valueError}</FormHelperText>}
          </FormControl>
        </div>
        <div className={styles.buttons}>
          <Button onClick={handleAbort} color="primary" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            color="primary"
            variant="contained"
            type="submit"
            disabled={valueInput === '' || !!valueError}
            onClick={handleSubmit}
          >
            Update Commitment
          </LoadingButton>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </div>
    </Dialog>
  );
}
